//withour apply the get session_id api
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { Loader2, Send, Sparkles } from 'lucide-react';
import { apiClient } from '@/core/api/client';
import { useRecaptchaGuestSession } from '@/features/guest-session/hooks/useRecaptchaGuestSession';
import { getGuestSessionToken } from '@/features/guest-session/utils/guestSessionStorage';
import { ApiRequestError } from '@/core/api/types';
import { logChatResponse } from '@/core/services/chatSession.service';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card } from '@/shared/components/ui/card';
import { Textarea } from '@/shared/components/ui/textarea';
import { JourneyProgress } from './JourneyProgress';
import { RetryButton } from './RetryButton';
import { getChatUserType, sendChatMessage } from '../../../core/services/chatService';
import type { ChatRequestPayload, ChatResponsePayload } from '../../../core/services/chatService';

type MessageError = {
  code?: string;
  retryable: boolean;
  retryAfter: number | null;
  retryText: string;
};

type Message = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  quickReplies?: string[];
  error?: MessageError;
};

type UserData = {
  urgency?: string;
  planningContext?: string;
  zipCode?: string;
  budget?: string;
  situation?: string;
};

type CareStageData = Record<string, unknown> | null;

const CHAT_SESSION_STORAGE_KEY = 'chat_session_id';

const staticChatResponse: ChatResponsePayload = {
  session_id: 'id_007',
  response: '',
  status: 'in_progress',
  classification: null,
};

const initialMessages: Message[] = [
  {
    id: '1',
    role: 'assistant',
    content:
      "Hello! I'm your bQuest Navigator. I'm here to help you find the right care and build a personalized plan. To get started, tell me a little about what's going on: who needs care, and what's prompting your search right now.",
  },
];

export function ChatInterface() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { recaptchaElement, ensureGuestSession } = useRecaptchaGuestSession();
  const [careStageData, setCareStageData] = useState<CareStageData>(null);
  const [chatResponse, setChatResponse] = useState<ChatResponsePayload>(staticChatResponse);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [returnToSummary, setReturnToSummary] = useState<{ sessionId: string; careStage: string } | null>(null);
  const [userData, setUserData] = useState<UserData>({
    urgency: staticChatResponse.classification?.care_mode,
    zipCode: staticChatResponse.classification?.zip_code,
    budget: staticChatResponse.classification?.budget_range,
    situation: staticChatResponse.classification?.summary,
  });
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isClassificationConfirmed =
    chatResponse.status === 'confirmed' || chatResponse.status === 'classification_ready';

  useEffect(() => {
    const savedSessionId = sessionStorage.getItem('bquest_nav_session_id');
    const savedCareStage = sessionStorage.getItem('bquest_nav_care_stage');
    if (savedSessionId) {
      setReturnToSummary({ sessionId: savedSessionId, careStage: savedCareStage ?? '' });
    }
  }, []);

  const isInitializedRef = useRef(false);
  useEffect(() => {
    if (!isInitializedRef.current) {
      if (messages.length > 0) {
        isInitializedRef.current = true;
      }
      return;
    }
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [input]);
  useEffect(() => {
    const restoreConversation = async () => {
      try {
        // FORCE NEW CHAT
        const forceNewChat = sessionStorage.getItem('force_new_chat');

        if (forceNewChat === 'true') {
          sessionStorage.removeItem('force_new_chat');

          setSessionId(null);

          setMessages(initialMessages);

          return;
        }

        // GET SESSION
        const savedSessionId = localStorage.getItem(CHAT_SESSION_STORAGE_KEY);

        // NEW USER
        if (!savedSessionId) {
          setMessages(initialMessages);

          return;
        }

        // Guests can only restore once a guest token exists in this tab; without
        // it the request would 401 and bounce to /login. Start fresh instead.
        if (!isAuthenticated && !getGuestSessionToken()) {
          setMessages(initialMessages);
          return;
        }

        // RESTORE SESSION
        setSessionId(savedSessionId);

        // CALL API
        const response = await apiClient.get(`/chat-engine/conversation/${savedSessionId}`);

        const data = response.data;

        // RESTORE OLD CHAT
        if (data.messages?.length > 0) {
          const formattedMessages = data.messages.map(
            (message: { id: string; role: string; content: string }) => ({
              id: message.id,

              role: message.role.toLowerCase() as 'user' | 'assistant',

              content: message.content,
            })
          );

          setMessages(formattedMessages);
        } else {
          setMessages(initialMessages);
        }
      } catch (error) {
        console.error('Failed to restore conversation:', error);

        setMessages(initialMessages);
      }
    };

    void restoreConversation();
  }, [isAuthenticated]);

  const handleSend = async (text?: string) => {
    const messageText = text || input.trim();
    if (!messageText || isTyping) {
      return;
    }
    const updatedConversation = [
      ...messages,
      {
        id: `${Date.now()}`,
        role: 'user' as const,
        content: messageText,
      },
    ];

    const requestPayload: ChatRequestPayload = {
      session_id: sessionId,

      status: chatResponse.status || 'IN_PROGRESS',

      user_type: getChatUserType(user?.role),

      message: messageText,

      confirmed: true,

      care_stage_data: careStageData || null,

      conversation: updatedConversation.map((message) => ({
        role: message.role,
        content: message.content,
      })),
    };

    setMessages(updatedConversation);

    setInput('');
    setIsTyping(true);

    try {
      // Bot-gate guests before the first AI call: solve invisible reCAPTCHA and
      // mint a guest session token (no-op for logged-in users / once minted).
      await ensureGuestSession(isAuthenticated);
      const responseData = await sendChatMessage(requestPayload);
      setCareStageData(responseData.care_stage_data ?? null);
      const nextSessionId = responseData.session_id || sessionId;


      if (nextSessionId) {
        const completeConversation = [
          ...requestPayload.conversation,
          ...(responseData.response
            ? [{ role: 'assistant' as const, content: responseData.response }]
            : []),
        ];
        void apiClient
          .post<{ received: true }>('/chat-engine/store-request', {
            ...requestPayload,
            session_id: nextSessionId,
            conversation: completeConversation,
          })
          .catch((err: unknown) => {
            console.error('[ChatEngine] Failed to store conversation:', err);
          });
      }
      setChatResponse(responseData);
      setSessionId(nextSessionId);
      if (
        nextSessionId &&
        responseData.status !== 'completed' &&
        responseData.status !== 'classification_ready'
      ) {
        localStorage.setItem(CHAT_SESSION_STORAGE_KEY, nextSessionId);
      }
      setUserData({
        urgency: responseData.classification?.care_mode,
        zipCode: responseData.classification?.zip_code,
        budget: responseData.classification?.budget_range,
        situation: responseData.classification?.summary,
      });

      if (responseData.response) {
        const quickReplies = responseData.quick_reply_options
          ? responseData.quick_reply_options.split(',').map((item: string) => item.trim())
          : [];

        setMessages((previous) => [
          ...previous,
          {
            id: `${Date.now()}-assistant`,
            role: 'assistant',
            content: responseData.response,
            quickReplies,
          },
        ]);
      }

      if (responseData.status === 'completed' || responseData.status === 'classification_ready') {
        const targetSessionId = nextSessionId ?? '';
        const targetCareStage = responseData.classification?.care_stage ?? '';
        sessionStorage.setItem('bquest_nav_session_id', targetSessionId);
        sessionStorage.setItem('bquest_nav_care_stage', targetCareStage);
        // Wait for session to be stored in DB before navigating so that
        // generate-journey-summary finds the complete session data
        await logChatResponse({
          user_id: user?.id ?? null,
          session_id: responseData.session_id,
          response: responseData.response,
          status: responseData.status,
          classification: responseData.classification,
          care_stage_data: responseData.care_stage_data,
        });
        if (responseData.classification?.is_possible === false) {
          navigate('/redesign-chatbot/concierge');
        } else {
          navigate('/redesign-chatbot/situation-summary', {
            state: { sessionId: targetSessionId, careStage: targetCareStage },
          });
        }
      }
    } catch (error) {
      console.error('Failed to send chat message:', error);
      const errorMessage =
        (error instanceof Error && error.message) ||
        'Something went wrong while contacting the care assistant. Please try again.';
      const apiError = error instanceof ApiRequestError ? error : null;
      setMessages((previous) => [
        ...previous,
        {
          id: `${Date.now()}-error`,
          role: 'assistant',
          content: errorMessage,
          error: {
            code: apiError?.code,
            retryable: apiError?.retryable ?? false,
            retryAfter: apiError?.retryAfter ?? null,
            retryText: messageText,
          },
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const startNewChat = () => {
    localStorage.removeItem(CHAT_SESSION_STORAGE_KEY);
    setSessionId(null);
    setCareStageData(null);
    setChatResponse(staticChatResponse);
    setMessages(initialMessages);
  };

  return (
    <div className="min-h-screen bg-background">
      {recaptchaElement}
      <JourneyProgress currentStep={isClassificationConfirmed ? 'summary' : 'intake'} />

      <div className="container mx-auto px-4 py-8">
        <div className="mx-auto w-full max-w-4xl">
          <div className="mb-6 text-center">
            <div className="mb-2 flex items-center justify-center gap-2">
              <Sparkles className="text-[#026857]" size={32} />
              <h1 className="text-2xl font-semibold text-gray-900">bQuest Navigator</h1>
            </div>
            <p className="text-sm text-gray-600">AI-powered care planning assistant</p>
          </div>

          <Card className="border-gray-200 shadow-sm">
            <div className="border-b border-gray-200 px-6 py-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="border-[#026857]/20 text-[#026857]">
                    Care Seeker
                  </Badge>
                  {userData.urgency && <Badge variant="secondary">{userData.urgency}</Badge>}
                  {userData.zipCode && <Badge variant="secondary">{userData.zipCode}</Badge>}
                  {userData.budget && <Badge variant="secondary">{userData.budget}</Badge>}
                </div>
                {returnToSummary && (
                  <Button
                    size="sm"
                    onClick={() =>
                      navigate('/redesign-chatbot/situation-summary', {
                        state: {
                          sessionId: returnToSummary.sessionId,
                          careStage: returnToSummary.careStage,
                        },
                      })
                    }
                    className="gap-1.5 bg-[#026857] text-white hover:bg-[#025247]"
                  >
                    View My Assessment
                  </Button>
                )}
              </div>
            </div>

            <div className="max-h-[520px] min-h-[420px] space-y-6 overflow-y-auto p-6">
              {messages.map((message) => (
                <div key={message.id}>
                  <div className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : ''}`}>
                    {message.role === 'assistant' && (
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#026857]">
                        <Sparkles size={20} className="text-white" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] rounded-2xl px-5 py-3 ${
                        message.role === 'user'
                          ? 'bg-[#026857] text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      <p className="text-sm leading-relaxed">{message.content}</p>
                    </div>
                  </div>

                  {message.role === 'assistant' &&
                    message.quickReplies &&
                    message.quickReplies.length > 0 && (
                      <div className="ml-14 mt-3 flex flex-wrap gap-2">
                        {message.quickReplies.map((reply) => (
                          <Badge
                            key={reply}
                            variant="outline"
                            className="cursor-pointer px-4 py-2 transition-colors hover:border-[#026857] hover:bg-[#026857] hover:text-white"
                            onClick={() => handleSend(reply)}
                          >
                            {reply}
                          </Badge>
                        ))}
                      </div>
                    )}

                  {message.role === 'assistant' && message.error?.retryable && (
                    <div className="ml-14 mt-3">
                      <RetryButton
                        retryAfter={message.error.retryAfter}
                        onRetry={() => void handleSend(message.error?.retryText)}
                      />
                    </div>
                  )}

                  {message.role === 'assistant' && message.error?.code === 'AI_CONTEXT_OVERFLOW' && (
                    <div className="ml-14 mt-3">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={startNewChat}
                        className="border-[#026857] text-[#026857] hover:bg-[#026857]/5 hover:text-[#026857]"
                      >
                        Start New Chat
                      </Button>
                    </div>
                  )}
                </div>
              ))}

              {isTyping && (
                <div className="flex gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#026857]">
                    <Sparkles size={20} className="text-white" />
                  </div>
                  <div className="rounded-2xl bg-gray-100 px-5 py-3">
                    <Loader2 className="animate-spin text-gray-500" size={20} />
                  </div>
                </div>
              )}

              <div ref={scrollRef} />
            </div>

            <div className="border-t border-gray-200 p-4">
              <div className="flex items-end gap-3">
                <Textarea
                  ref={textareaRef}
                  placeholder="Type your response..."
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      void handleSend();
                    }
                  }}
                  className="min-h-[44px] max-h-[120px] flex-1 resize-none rounded-xl border-gray-300 focus:border-[#026857] focus:ring-[#026857]"
                  rows={1}
                />
                <Button
                  onClick={() => void handleSend()}
                  disabled={!input.trim() || isTyping}
                  className="h-[44px] w-[44px] flex-shrink-0 rounded-xl bg-[#026857] p-0 hover:bg-[#025247]"
                >
                  <Send size={18} />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
